import { useMemo, useRef, useState } from "react";
import type {
  Collision,
  CollisionDetection,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  KeyboardCoordinateGetter,
} from "@dnd-kit/core";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  defaultKeyboardCoordinateGetter,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { observer } from "mobx-react-lite";
import type { IssueStatus, PMIssue } from "@/domains/pm/types";
import { useIssuesStore } from "@/stores/pm/hooks/usePMStore";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { ViewerDocumentData } from "@/domains/documents/hooks/useDocumentViewer";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { useStatusCatalogue } from "@/domains/pm/statuses/statusCatalogueContext";
import { useStatusAdmin } from "@/domains/pm/statuses/statusAdminContext";
import { resolveStatus, visibleStatuses } from "@/domains/pm/statuses/statusCatalogue";
import { assignCustomSortOrders, moveColumn } from "@/domains/pm/statuses/statusOrder";
import { listIssueStatuses, updateIssueStatus } from "@/stores/pm/repository";
import { friendlyIssueError } from "@/domains/pm/utils/issueErrors";
import { usePermissions } from "@/hooks/usePermissions";
import { useIssuePlacements } from "@/domains/pm/hooks/useIssuePlacements";
import { arrangeIds } from "@/domains/pm/utils/issuePlacement";
import { columnOf, moveCard, type BoardColumns } from "@/domains/pm/utils/kanbanCardMove";
import { useStableCallback } from "@/hooks/useStableCallback";
import { KanbanColumn } from "./KanbanColumn";
import { KanbanNewStatusColumn } from "./KanbanNewStatusColumn";
import { KanbanCardFace } from "./KanbanCard";
import { useIssueLoadNotice } from "./useIssueLoadNotice";
import { track } from "@/lib/posthog";
import { usePermissionDenied } from "@/hooks/usePermissionDenied";
import type { DraftIssueViewProps } from "./issueDraftView";
import { isDraftIssueId } from "@/domains/pm/issueDraft";

interface KanbanBoardProps extends DraftIssueViewProps {
  buildingId: number;
  openViewer: (doc: ViewerDocumentData) => Promise<void>;
  /** Click handler — toggles the right-side detail panel for the card. */
  onSelectIssue: (issue: PMIssue) => void;
  /** Open the New Issue form pre-set to a column's status. */
  onAddIssue: (status: IssueStatus) => void;
  /** The project in scope, or null for "All projects". Decides which custom
   *  statuses get a column, and which drops are legal. */
  buildingProjectId?: number | null;
  /** Told when a card or column is picked up and put down, so the page can
   *  hold back a realtime reload that would replace the board mid-gesture. */
  onDragActiveChange?: (active: boolean) => void;
}

/** Arrow keys walk a card between its neighbours; a column keeps the plain
 *  step it had, since its drop targets are the other columns' headers. */
const boardKeyboardCoordinates: KeyboardCoordinateGetter = (event, args) =>
  args.context.active?.data.current?.kind === "card"
    ? sortableKeyboardCoordinates(event, args)
    : defaultKeyboardCoordinateGetter(event, args);

const KanbanBoard = observer(function KanbanBoard({
  buildingId,
  openViewer,
  onSelectIssue,
  onAddIssue,
  buildingProjectId,
  onDragActiveChange,
  draftIssues,
  activeDraftId,
  savingDraftIds,
  onSelectDraft,
}: KanbanBoardProps) {
  const issuesStore = useIssuesStore();
  const { t } = useLanguage();
  const { catalogue, buildingId: catalogueBuildingId } = useStatusCatalogue();
  const { canManagePMOnProject } = usePermissions();
  const issues = issuesStore.issues;
  const issuesById = useMemo(
    () => new Map<number, PMIssue>(issues.map((issue) => [issue.id, issue])),
    [issues]
  );

  /**
   * Whether this user may store a position for one card. An id nobody on this
   * board can account for — a card a filter hid, a card that has since moved
   * away — is not writable: the write would be refused and take the whole
   * column's order with it.
   */
  const mayArrangeCard = (id: number) => {
    // A draft has no row to place; its id must never reach a placement write.
    if (isDraftIssueId(id)) return false;
    const issue = issuesById.get(id);
    return !!issue && canManagePMOnProject(issue.building_project_id);
  };

  // The column order within a status is shared by everyone on the project.
  const { ranksFor, saveBand } = useIssuePlacements(buildingId, "board", "status", mayArrangeCard);
  // Reordering writes a status ROW, so it needs the row's id — the catalogue
  // carries descriptors, not ids. Same query key as the catalogue's own, so
  // this is the cached rows rather than a second fetch.
  const { data: customRows = [] } = useQuery({
    queryKey: ["pm-issue-statuses", catalogueBuildingId],
    queryFn: () => listIssueStatuses(catalogueBuildingId as number),
    enabled: catalogueBuildingId !== undefined,
    staleTime: 60_000,
  });
  // One project's board can be rearranged; "All projects" shows several
  // projects' columns at once and has no single order to write.
  const mayReorder = buildingProjectId != null && canManagePMOnProject(buildingProjectId);
  // "New status" as the board's last column. The page offers it only while one
  // project is in scope and the user may manage its statuses — a status
  // belongs to one project — and My Issues mounts no provider at all.
  const statusAdmin = useStatusAdmin();
  // The columns are partitioned client-side from the loaded issues, so a
  // still-filling or ceiling-capped scope leaves columns short. Say so.
  const loadNotice = useIssueLoadNotice();

  // Activation distance lets a card receive a plain click (to open the
  // detail panel) without the pointer-down immediately starting a drag.
  // The user has to pull the card 5px before @dnd-kit takes over.
  // The keyboard sensor makes the instruction dnd-kit already announces on
  // every draggable ("press the space bar… use the arrow keys") actually true —
  // without it the board advertised a gesture nobody could perform.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: boardKeyboardCoordinates })
  );

  // Every column always renders. The status filter is applied server-side, so
  // a filtered-out status simply has no cards — and the empty column, beside
  // the filter chip in the toolbar, explains itself. Removing the column
  // instead would change the board's shape under the user.
  const grouped = issuesStore.issuesByStatus;
  const columns = useMemo(
    () => visibleStatuses(catalogue, buildingProjectId),
    [catalogue, buildingProjectId]
  );
  // New issues still being written, at the top of their status's column. A
  // status with no column here (another project's, say) puts the draft in the
  // first column rather than nowhere. Drafts are kept out of `arranged` and
  // `issuesById`, so no drag, drop or stored column order can reach them.
  const draftsByColumn = useMemo(() => {
    const byColumn = new Map<string, PMIssue[]>();
    const firstKey = columns[0]?.key;
    for (const draft of draftIssues ?? []) {
      const key = columns.some((c) => c.key === draft.status) ? draft.status : firstKey;
      if (key === undefined) continue;
      byColumn.set(key, [...(byColumn.get(key) ?? []), draft]);
    }
    return byColumn;
  }, [draftIssues, columns]);
  const selectDraft = useStableCallback((draftId: number) => onSelectDraft?.(draftId));

  // The stored arrangement over the order the store holds; a card nobody
  // placed goes to the bottom of its column.
  const arranged = useMemo<BoardColumns>(
    () =>
      Object.fromEntries(
        columns.map((column) => [
          column.key,
          arrangeIds(
            (grouped[column.key] ?? []).map((issue) => issue.id),
            ranksFor(column.key),
            null
          ),
        ])
      ),
    [columns, grouped, ranksFor]
  );

  /**
   * The board as the user is holding it: cards moved across columns while a
   * drag is live, then the dropped order until the store and the saved
   * arrangement have both caught up — so a dropped card stays where it landed
   * rather than jumping back for a round trip.
   */
  const [held, setHeld] = useState<BoardColumns | null>(null);
  const [activeCardId, setActiveCardId] = useState<number | null>(null);
  const shownRef = useRef<BoardColumns>({});
  const shown = keepUnchangedColumns(shownRef.current, held ? reconcile(held, arranged) : arranged);
  shownRef.current = shown;
  /** What the dragged card was last over — kept for the moments the pointer is
   *  over nothing, such as the gap between two columns. */
  const lastOver = useRef<Collision[]>([]);

  const mayMoveCard = (issue: PMIssue) => canManagePMOnProject(issue.building_project_id);
  const denyPermission = usePermissionDenied();
  // Cards are memoised, so the handlers they are handed keep their identity —
  // the page's own handlers are new on every render, and passing those through
  // re-rendered every card on the board whenever an issue was opened.
  const refuseMove = useStableCallback((issue: PMIssue) => {
    void denyPermission("manageIssues", issue.building_project_id);
  });
  const selectCard = useStableCallback(onSelectIssue);
  const openCardViewer = useStableCallback(openViewer);

  /**
   * Whether this user may store a column's order. A drop writes the order of
   * the WHOLE column, and "All projects" can put several projects' cards in
   * one — so it takes MANAGE_PM on every one of them, exactly as the Gantt's
   * bands do (`arrangeableBands`). Dragging a single card is a weaker check by
   * design: the status change is the card owner's to make.
   */
  const mayArrangeColumn = (cardIds: readonly number[]) => cardIds.every(mayArrangeCard);

  /**
   * Cards and columns are both dragged here, and each should only ever land on
   * its own kind. A card first finds the column under the pointer, then the
   * nearest card in it — a column's card area is a large target and would
   * otherwise win over the card the pointer is actually on.
   */
  const collisionDetection: CollisionDetection = (args) => {
    const kind = args.active.data.current?.kind;
    if (kind === "column") {
      return closestCenter({
        ...args,
        droppableContainers: args.droppableContainers.filter((container) =>
          ["column", "cards"].includes(container.data.current?.kind)
        ),
      });
    }
    const candidates = args.droppableContainers.filter((container) =>
      ["card", "cards"].includes(container.data.current?.kind)
    );
    const within = pointerWithin({ ...args, droppableContainers: candidates });
    // A pointer over nothing — the gap between columns, the header row — keeps
    // the card where it was. Falling back to the dragged card's rectangle there
    // made it hop into whichever neighbour that rectangle overlapped most, and
    // back again a few pixels later. Only the keyboard, which has no pointer,
    // needs the rectangle.
    if (within.length === 0 && args.pointerCoordinates) return lastOver.current;
    const hits =
      within.length > 0 ? within : rectIntersection({ ...args, droppableContainers: candidates });
    const column = hits.find((hit) => hit.data?.droppableContainer.data.current?.kind === "cards");
    if (!column) return (lastOver.current = hits);
    const cardIds = new Set(shownRef.current[String(column.id)] ?? []);
    const cardsInColumn = candidates.filter(
      (container) => container.data.current?.kind === "card" && cardIds.has(Number(container.id))
    );
    if (cardsInColumn.length === 0) return (lastOver.current = [column]);
    return (lastOver.current = closestCenter({ ...args, droppableContainers: cardsInColumn }));
  };

  function handleDragStart(event: DragStartEvent) {
    onDragActiveChange?.(true);
    lastOver.current = [];
    if (event.active.data.current?.kind !== "card") return;
    setActiveCardId(Number(event.active.id));
    setHeld(arranged);
  }

  /** Crossing into another column moves the card there at once, so that
   *  column's cards make room. Order WITHIN a column is left to the sortable
   *  strategy until the drop, which is what keeps it from jittering. */
  function handleDragOver({ active, over }: DragOverEvent) {
    if (active.data.current?.kind !== "card" || !over) return;
    const cardId = Number(active.id);
    const current = shownRef.current;
    const overColumn = over.data.current?.kind === "cards";
    const targetColumn = overColumn ? String(over.id) : columnOf(current, Number(over.id));
    if (!targetColumn || targetColumn === columnOf(current, cardId)) return;
    const dragged = active.rect.current.translated;
    const below =
      !!dragged && dragged.top + dragged.height / 2 > over.rect.top + over.rect.height / 2;
    setHeld(
      moveCard(current, cardId, overColumn ? targetColumn : Number(over.id), overColumn, below)
    );
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    // A reload held back during the drag may run now: the write below holds
    // its edit before any read could land, so the read cannot undo the drop.
    onDragActiveChange?.(false);

    // Two things drag on this board, told apart by the ACTIVE item's kind.
    if (active.data.current?.kind === "column") {
      if (over) handleColumnDrop(active.data.current.status as IssueStatus, over.id);
      return;
    }

    setActiveCardId(null);
    const cardId = Number(active.id);
    const board = shownRef.current;
    const column = columnOf(board, cardId);
    const issue = issuesById.get(cardId);
    if (!over || !column || !issue) {
      setHeld(null);
      return;
    }

    // Within the column, land where the sortable strategy was showing it.
    let dropped = board;
    const list = board[column];
    const overIndex = list.indexOf(Number(over.id));
    const fromIndex = list.indexOf(cardId);
    if (overIndex !== -1 && overIndex !== fromIndex) {
      dropped = { ...board, [column]: arrayMove(list, fromIndex, overIndex) };
    }

    const newStatus = column as IssueStatus;
    const statusChanged = newStatus !== issue.status;
    const order = dropped[column];
    if (!statusChanged && sameIds(order, arranged[column] ?? [])) {
      setHeld(null);
      return;
    }

    if (statusChanged) {
      // A custom status belongs to ONE project, and this board can show
      // several. Dropping a card into a column its own project does not define
      // would be rejected by the database trigger, so refuse it here where we
      // can say why rather than letting the write fail and the card snap back
      // unexplained.
      const target = resolveStatus(catalogue, issue.building_project_id, newStatus);
      if (target.custom && target.buildingProjectId !== issue.building_project_id) {
        toast.error(t.pm.issues.statuses.notInThisProject);
        setHeld(null);
        return;
      }
      // Optimistic update. Status is filtered SERVER-side, so a card dropped
      // into a status the filter excludes must leave the board and start
      // counting toward "N hidden by filters" — `updateIssue` refetches for
      // exactly that case (IssuesStore.writeEscapesFilters), which is also why
      // this handler doesn't reconcile by hand. The catch is the rollback.
      issuesStore.updateIssueOptimistic(cardId, { status: newStatus });
      issuesStore
        .updateIssue(cardId, { status: newStatus })
        .then(() => {
          track("issue_status_changed", {
            from_status: issue.status,
            to_status: newStatus,
            bulk: false,
          });
        })
        .catch(() => {
          issuesStore.loadIssues(buildingId);
        });
    }

    // The order is shared with every project whose cards sit in this column, so
    // a user who may not manage all of them cannot store it — the write would
    // be refused by RLS after the cards had already moved and the column would
    // roll back unexplained. Withhold the order only; the status change above
    // was this user's to make and stands. The card then takes its default
    // place in the column rather than sitting somewhere nothing recorded — and
    // is TOLD so, because the card visibly moves back and a silent snap reads
    // as the board being broken. The drag itself cannot be blocked up front the
    // way the Gantt's is: carrying a card into another column is still this
    // user's to do, and only the drop knows where it landed.
    if (!mayArrangeColumn(order)) {
      toast.error(t.pm.issues.statuses.orderNotSaved);
      setHeld(null);
      return;
    }

    setHeld(dropped);
    void saveBand(column, order).finally(() =>
      setHeld((current) => (current === dropped ? null : current))
    );
  }

  function handleDragCancel() {
    onDragActiveChange?.(false);
    setActiveCardId(null);
    setHeld(null);
  }

  /**
   * Reorder the board. Only custom statuses are stored, so this renumbers the
   * project's customs around the built-in anchors and writes the ones that
   * moved — see `statusOrder.ts` for why it renumbers rather than nudging.
   */
  function handleColumnDrop(movedKey: IssueStatus, overId: string | number) {
    // A column can be dropped on another column's header (`colhead:`) or on its
    // card area (the bare status key) — both mean the same place.
    const raw = String(overId);
    const targetKey = raw.startsWith("colhead:") ? raw.slice("colhead:".length) : raw;
    if (targetKey === movedKey) return;
    const ordered = visibleStatuses(catalogue, buildingProjectId);
    const toIndex = ordered.findIndex((d) => d.key === targetKey);
    if (toIndex === -1) return;

    const changed = assignCustomSortOrders(moveColumn(ordered, movedKey, toIndex));
    if (changed.size === 0) return;

    void Promise.all(
      [...changed.entries()].map(([key, sort_order]) => {
        const row = customRows.find(
          (r) => r.key === key && r.building_project_id === buildingProjectId
        );
        return row ? updateIssueStatus(row.id, { sort_order }) : Promise.resolve();
      })
    ).catch((e) => toast.error(friendlyIssueError(e, t)));
  }

  const activeIssue = activeCardId != null ? issuesById.get(activeCardId) : undefined;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      {loadNotice && (
        <p className="px-7 pt-3 text-xs text-muted-foreground" aria-live="polite">
          {loadNotice}
        </p>
      )}
      <div className="flex min-h-full gap-4 overflow-x-auto px-7 py-4">
        {columns.map((column) => (
          <KanbanColumn
            key={column.key}
            status={column.key}
            label={column.label}
            color={column.dotColor}
            reorderable={column.custom && mayReorder}
            cardIds={shown[column.key] ?? NO_CARDS}
            issues={(shown[column.key] ?? NO_CARDS).flatMap((id) => issuesById.get(id) ?? [])}
            canDragCard={mayMoveCard}
            onBlockedDrag={refuseMove}
            openViewer={openCardViewer}
            onSelectIssue={selectCard}
            onAddIssue={onAddIssue}
            draftIssues={draftsByColumn.get(column.key)}
            activeDraftId={activeDraftId}
            savingDraftIds={savingDraftIds}
            onSelectDraft={selectDraft}
          />
        ))}
        {statusAdmin && <KanbanNewStatusColumn onCreate={() => statusAdmin.createStatus()} />}
      </div>
      {/* The copy under the pointer. Kept outside the columns, so carrying a
          card into another column moves its placeholder, never this. */}
      <DragOverlay dropAnimation={{ duration: 200, easing: "cubic-bezier(0.33, 1, 0.68, 1)" }}>
        {activeIssue ? <KanbanCardFace issue={activeIssue} openViewer={openViewer} lifted /> : null}
      </DragOverlay>
    </DndContext>
  );
});

/**
 * The held board, brought up to date with what has changed underneath it: a
 * card that has since left the scope is dropped, and a card that has arrived
 * takes its arranged place at the end of the column it arrived in.
 */
function reconcile(held: BoardColumns, arranged: BoardColumns): BoardColumns {
  const present = new Set(Object.values(arranged).flat());
  const placed = new Set(Object.values(held).flat());
  return Object.fromEntries(
    Object.keys(arranged).map((key) => [
      key,
      [
        ...(held[key] ?? []).filter((id) => present.has(id)),
        ...arranged[key].filter((id) => !placed.has(id)),
      ],
    ])
  );
}

const NO_CARDS: number[] = [];

/**
 * `next`, with every column whose cards have not changed handed back as the
 * array it had before. A column's ids are its SortableContext's items, and
 * dnd-kit re-renders every card in a context whose items array is new — so a
 * fresh array per render redrew the whole board on every issue opened and every
 * reload, however little had changed.
 */
function keepUnchangedColumns(previous: BoardColumns, next: BoardColumns): BoardColumns {
  return Object.fromEntries(
    Object.entries(next).map(([key, ids]) => {
      const before = previous[key];
      return [key, before && sameIds(before, ids) ? before : ids];
    })
  );
}

function sameIds(a: readonly number[], b: readonly number[]): boolean {
  return a.length === b.length && a.every((id, index) => id === b[index]);
}

export { KanbanBoard };
